import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  MdOutlineArrowBack,
  MdOutlineEdit,
  MdOutlineLink,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { EVENTS } from "../config/apiConfig";
import { Badge, Card, ErrorBlock, SkeletonRows } from "../components/crm/Primitives";
import { formatMoney, formatNumber } from "../components/crm/crmHelpers";
import EventSignups from "../components/events/EventSignups";
import EventCouponCard from "../components/events/EventCouponCard";
import EventFormModal from "../components/events/EventFormModal";
import LinkClientModal from "../components/events/LinkClientModal";
import Modal from "../components/modals/Modal";
import {
  EVENT_STATUS_META,
  formatEventDates,
  NO_EVENT_ID,
} from "../components/events/eventHelpers";

/*
  Um evento: os números de quantas clientes vieram, o cupom, e os cadastros com
  o botão de cobrar o plano (a mesma lista de antes, só deste evento).

  `/eventos/sem-evento` é o balde de quem passou pelo onboarding do evento e não
  caiu em nenhum evento cadastrado: criar o evento depois ainda os recolhe, e
  enquanto isso a equipe consegue cobrá-los.
*/

const REFRESH_MS = 30_000;

const MetricTile = ({ label, value, hint }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      {label}
    </p>
    <p className="mt-1 text-2xl font-bold leading-none text-slate-900">
      {formatNumber(value)}
    </p>
    {hint ? <p className="mt-1 text-[11px] text-slate-500">{hint}</p> : null}
  </div>
);

const BackLink = () => (
  <Link
    to="/eventos"
    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-pink-700"
  >
    <MdOutlineArrowBack size={14} /> Eventos
  </Link>
);

const EventPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNoEvent = id === NO_EVENT_ID;

  // "edit" | "link" | "delete" | null
  const [dialog, setDialog] = useState(null);
  const [notice, setNotice] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const query = useQuery(
    ["events", "detail", id],
    async () => {
      const { data } = await axios.get(`${EVENTS.ONE}/${id}`);
      return data.data;
    },
    {
      enabled: !isNoEvent,
      refetchInterval: REFRESH_MS,
      refetchIntervalInBackground: false,
      retry: false,
    }
  );

  useEffect(() => {
    if (!notice) return undefined;

    const timeout = setTimeout(() => setNotice(null), 8_000);
    return () => clearTimeout(timeout);
  }, [notice]);

  const remove = useMutation(() => axios.delete(`${EVENTS.ONE}/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["events"]);
      navigate("/eventos", { replace: true });
    },
    onError: (error) =>
      setDeleteError(
        error.response?.data?.message || "Não foi possível apagar o evento."
      ),
  });

  if (isNoEvent) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Card className="p-4">
          <h1 className="text-lg font-bold text-slate-900">Cadastros sem evento</h1>
          <p className="mt-1 text-sm text-slate-500">
            Quem passou pelo onboarding do evento e ainda não está em nenhum
            evento cadastrado. Crie o evento (com as datas certas) ou vincule à
            mão pela tela do evento. Enquanto isso, dá para cobrar daqui.
          </p>
        </Card>
        <EventSignups eventId="none" />
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <BackLink />
        <SkeletonRows rows={3} />
      </div>
    );
  }

  if (query.error) {
    const notFound = query.error.response?.status === 404;

    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorBlock
          message={
            notFound
              ? "Este evento não existe mais."
              : query.error.response?.data?.message ||
                "Não foi possível carregar o evento."
          }
          onRetry={notFound ? null : query.refetch}
        />
      </div>
    );
  }

  const event = query.data;
  const status = EVENT_STATUS_META[event.status];
  const { metrics } = event;
  const origin = [
    metrics.bySource.ONBOARDING ? `${metrics.bySource.ONBOARDING} pelo onboarding` : null,
    metrics.bySource.COUPON ? `${metrics.bySource.COUPON} pelo cupom` : null,
    metrics.bySource.MANUAL ? `${metrics.bySource.MANUAL} à mão` : null,
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      <BackLink />

      {notice ? (
        <div
          role="status"
          className="flex items-start justify-between gap-3 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200"
        >
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="shrink-0 text-xs font-semibold underline"
          >
            Fechar
          </button>
        </div>
      ) : null}

      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="break-words text-xl font-bold text-slate-900">
                {event.name}
              </h1>
              <Badge tone={status.tone}>{status.label}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {formatEventDates(event.startsAt, event.endsAt)}
              {" · "}
              {event.onboardingLabel
                ? `Filtra pelo onboarding: ${event.onboardingLabel}`
                : "Sem onboarding (só cupom e vínculo à mão)"}
            </p>
            {event.description ? (
              <p className="mt-1 whitespace-pre-line text-xs text-slate-500">
                {event.description}
              </p>
            ) : null}

            {/* Quem cobra no balcão, com o que cada um já cobrou. */}
            {event.operators.length ? (
              <ul
                aria-label="Operadores"
                className="mt-2 flex flex-wrap items-center gap-1.5"
              >
                {event.operators.map((operator) => (
                  <li
                    key={operator.id}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700 ring-1 ring-inset ring-slate-200"
                  >
                    <strong>{operator.name}</strong>
                    {operator.charges
                      ? ` · ${operator.charges} cobrança(s) · ${formatMoney(operator.received)}`
                      : " · nada cobrado ainda"}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-slate-500">
                Sem operadores: a cobrança não pergunta quem cobrou.{" "}
                <button
                  type="button"
                  onClick={() => setDialog("edit")}
                  className="font-semibold text-pink-700 underline"
                >
                  Cadastrar operadores
                </button>
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setDialog("link")}
              className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-3.5 py-2 text-sm font-bold text-white hover:bg-pink-700"
            >
              <MdOutlineLink size={16} /> Vincular cliente
            </button>
            <button
              type="button"
              onClick={() => setDialog("edit")}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <MdOutlineEdit size={16} /> Editar
            </button>
            {metrics.clients === 0 ? (
              <button
                type="button"
                onClick={() => {
                  setDeleteError(null);
                  setDialog("delete");
                }}
                className="inline-flex items-center rounded-xl border border-rose-200 px-3.5 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
              >
                Apagar
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
          <MetricTile
            label="Vieram pelo evento"
            value={metrics.clients}
            hint={origin.length ? origin.join(" · ") : "Ninguém vinculada ainda"}
          />
          <MetricTile
            label="Pagantes hoje"
            value={metrics.payingNow}
            hint="Pelo estágio no CRM, venham do balcão ou de compra depois"
          />
          <MetricTile
            label="Resgates do cupom"
            value={metrics.couponRedemptions}
            hint={event.coupon ? event.coupon.code : "Sem cupom neste evento"}
          />
        </div>
      </Card>

      <EventCouponCard event={event} />

      <EventSignups eventId={event.id} />

      {dialog === "edit" ? (
        <EventFormModal
          event={event}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            setNotice("Evento salvo.");
            queryClient.invalidateQueries(["events"]);
          }}
        />
      ) : null}

      {dialog === "link" ? (
        <LinkClientModal
          event={event}
          onClose={() => setDialog(null)}
          onLinked={(client) => {
            setDialog(null);
            setNotice(`${client.name} agora é cliente deste evento.`);
            queryClient.invalidateQueries(["events"]);
            queryClient.invalidateQueries(["crm"]);
            queryClient.invalidateQueries(["client", "detail", String(client.id)]);
          }}
        />
      ) : null}

      <Modal
        isOpen={dialog === "delete"}
        onClose={() => (remove.isLoading ? null : setDialog(null))}
        title="Apagar evento"
        subtitle={event.name}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDialog(null)}
              disabled={remove.isLoading}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isLoading}
              className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-60"
            >
              {remove.isLoading ? "Apagando..." : "Sim, apagar"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">
          Apagar o evento &quot;{event.name}&quot;? Ele ainda não tem clientes,
          então nenhum número se perde.
        </p>
        {deleteError ? (
          <p role="alert" className="mt-2 text-sm font-semibold text-rose-700">
            {deleteError}
          </p>
        ) : null}
      </Modal>
    </div>
  );
};

export default EventPage;
